if (!customElements.get('all-destinations')) {
  customElements.define(
    'all-destinations',
    class extends HTMLElement {
      constructor() {
        super();
        this.groups = this.querySelectorAll('.all-destinations__group');
        this.search = this.querySelector('input[name="destinations-search"]');
        this.boundHandleGroupClick = this.handleGroupClick.bind(this);
        this.boundHandleSearchInput = this.handleSearchInput.bind(this);
        this.debouncedSearchInput = this.debounce(this.boundHandleSearchInput, 150);
        this.noSearchResults = this.querySelector('.no__search-results');
        this.boundInit = this.initialize.bind(this);
        this.scrollContainer = this.querySelector('.group__container');
        this.allLists = this.querySelectorAll('.landing__page-list');
        this.currentGroup = 'popular';
      }

      connectedCallback() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', this.boundInit, { once: true });
          return;
        }
        this.initialize();
      }

      initialize() {
        this.groups.forEach((group) => {
          group.addEventListener('click', this.boundHandleGroupClick);
        });

        this.setScrollContainer();

        this.aggregateAllDestinations();

        this.activeCurrency = window?.Shopify?.currency;
        // this.updatePricing();

        this.search?.addEventListener('input', this.debouncedSearchInput);
        this.search?.addEventListener('change', this.boundHandleSearchInput);
        this.search?.addEventListener('blur', this.boundHandleSearchInput);

        this.params = new URLSearchParams(window.location.search);
        this.searchParams = this.params.get('q');
        if (this.searchParams) {
          this.search.value = this.searchParams;
          this.searchTerm = this.sanitizeSearchTerm(this.searchParams);
          // this.search?.value = this.searchTerm;
          this.showMatchingDestinations(this.searchTerm);
        }
      }

      setScrollContainer() {
        if (!this.scrollContainer) return;
        requestAnimationFrame(() => {
          this.scrollContainer.scrollLeft =
            this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;
        });
      }

      aggregateAllDestinations() {
        // get all the links from the other tabs
        // loop through the links and if they are unique, clone them and append them to a document fragment
        // append the document fragment to the all destinations list

        const allLinks = this.querySelectorAll('.landing__page-list a');
        const allDestinationsContainer = this.querySelector('.all__list');

        if (!allLinks || !allDestinationsContainer) return;

        allDestinationsContainer.innerHTML = '';
        const seen = new Set();

        const dedupedLinks = [...allLinks]
          .filter((link) => {
            const title = link.querySelector('li')?.dataset.title;
            if (!title || seen.has(title)) return false;
            seen.add(title);
            return true;
          })
          .sort((a, b) => {
            const aTitle = a.querySelector('li')?.dataset.title || '';
            const bTitle = b.querySelector('li')?.dataset.title || '';
            return aTitle.localeCompare(bTitle);
          });

        dedupedLinks.forEach((link) => {
          allDestinationsContainer.appendChild(link.cloneNode(true));
        });
      }

      /**
       * Returns a new function that will debounce the execution of the given function
       * by the given delay in milliseconds.
       *
       * @param {function} func - The function to debounce
       * @param {number} delay - The delay in milliseconds
       * @returns {function} - The debounced function
       */
      debounce(func, delay) {
        let timer = null;
        return (...args) => {
          if (timer) {
            clearTimeout(timer);
          }
          timer = setTimeout(() => {
            func(...args);
          }, delay);
        };
      }

      handleSearchInput() {
        this.searchTerm = this.sanitizeSearchTerm(this.search?.value);

        if (this.searchTerm && this.searchTerm.length > 0) {
          this.handleGroupClick('all');
        } else if (this.currentGroup && this.searchTerm.length === 0) {
          this.handleGroupClick(this.currentGroup);
        }

        this.showMatchingDestinations(this.searchTerm);
      }

      sanitizeSearchTerm(searchTerm) {
        return searchTerm?.toLowerCase().replace(/[^a-z0-9\s]/g, '') || '';
      }

      showMatchingDestinations(searchTerm) {
        const hasSearchTerm = Boolean(searchTerm?.trim());
        this.allDestinationLinks = this.querySelectorAll('.landing__page-list a');

        this.allDestinationLinks?.forEach((link) => {
          link.classList.add('hidden');
          if (!hasSearchTerm) {
            link.classList.remove('hidden');

            return;
          }

          const countriesCovered = link.dataset?.countries;
          if (!countriesCovered) return;

          const countriesCoveredArr = this.prepareCountriesArray(countriesCovered);

          const matchFound =
            this.isTitleMatch(link, searchTerm) ||
            this.isCountryMatch(countriesCoveredArr, searchTerm) ||
            this.isFuzzyTitleMatch(link, searchTerm) ||
            this.isFuzzyCountryMatch(countriesCoveredArr, searchTerm);

          if (matchFound) {
            link.classList.remove('hidden');
            link.classList.add('active');
          }
        });

        this.updateNoSearchResultsState(hasSearchTerm);
      }

      updateNoSearchResultsState(hasSearchTerm) {
        this.noSearchResults?.classList.remove('active');
        if (!hasSearchTerm) return;

        const activeList = this.querySelector('.landing__page-list.active');
        if (!activeList) return;

        const hasVisibleLinks = activeList.querySelector('a:not(.hidden)');
        if (hasVisibleLinks) return;

        const activeGroup = this.querySelector('.all-destinations__group.active');
        if (!activeGroup) return;

        this.noSearchResults.innerHTML = `No search results found in '${activeGroup.innerText.toLowerCase()}'. Please try searching another country or region.`;
        this.noSearchResults.classList.add('active');
        activeList.appendChild(this.noSearchResults);
      }

      prepareCountriesArray(countriesCovered) {
        return countriesCovered.split('|').map((country) => country.toLowerCase());
      }

      isTitleMatch(link, searchTerm) {
        const title = link.querySelector('li')?.dataset.title || '';
        return title.includes(searchTerm);
      }

      isCountryMatch(countriesCoveredArr, searchTerm) {
        return countriesCoveredArr.some((country) => country.includes(searchTerm));
      }

      isFuzzyTitleMatch(link, searchTerm) {
        const searchTermArr = searchTerm.split(' ') || [];
        const title = link.querySelector('li')?.dataset.title || '';
        const titleArr = title.split('-') || [];
        return titleArr.some((title) =>
          searchTermArr.some((searchTermWord) => this.fuzzyMatch(title, searchTermWord)),
        );
      }

      isFuzzyCountryMatch(countriesCoveredArr, searchTerm) {
        const searchTermArr = searchTerm.split(' ') || [];
        return countriesCoveredArr.some((country) => {
          const countryWordArr = country.split(' ') || [];
          return countryWordArr.some((countryWord) =>
            searchTermArr.some((searchTermWord) => this.fuzzyMatch(countryWord, searchTermWord)),
          );
        });
      }

      fuzzyMatch(strA, strB) {
        if (!strA || !strB) return false;

        const normalizedStrA = strA.trim().toLowerCase();
        const normalizedStrB = strB.trim().toLowerCase();
        const editDistance = levenshteinDistance(normalizedStrA, normalizedStrB);

        if (normalizedStrA.length >= 6 && normalizedStrB.length >= 6) {
          let commonPrefixLength = 0;
          const minLength = Math.min(normalizedStrA.length, normalizedStrB.length, 2);
          while (
            commonPrefixLength < minLength &&
            normalizedStrA[commonPrefixLength] === normalizedStrB[commonPrefixLength]
          ) {
            commonPrefixLength += 1;
          }

          // Avoid broad false-positives like "africa" matching "america",
          // while still allowing very-close typos on long words.
          if (commonPrefixLength < 2 && editDistance > 1) return false;

          // For long words with 2+ edits, require the ending character to align.
          // This blocks cases like "france" matching "french" while keeping
          // typo matches such as "frnace" -> "france".
          if (editDistance >= 2 && normalizedStrA.at(-1) !== normalizedStrB.at(-1)) return false;
        }

        const threshold = normalizedStrA.length < 5 || normalizedStrB.length < 5 ? 2 : 3;
        return editDistance < threshold;
      }

      handleGroupClick(event) {
        const clickedGroup = event.currentTarget || this.querySelector(`[data-tab=${event}]`);

        if (event.currentTarget) {
          this.currentGroup = clickedGroup.dataset.tab;
        }

        const parentEl = this.querySelector('.group__container');
        if (clickedGroup && parentEl) {
          parentEl.scrollTo({
            left: clickedGroup.offsetLeft - 20,
            behavior: 'smooth',
          });
        }

        this.groups.forEach((group) => {
          group.classList.toggle('active', group === clickedGroup);
          if (group === clickedGroup) {
            this.handleGroupListDisplay(group);
          }
        });
      }

      handleGroupListDisplay(group) {
        const listToShow = this.querySelector(`#${group.dataset.tab}-list`);
        if (!listToShow) return;

        this.allLists?.forEach((list) => {
          list.classList.remove('active');
          if (list === listToShow) {
            list.classList.add('active');
          }
        });

        this.updateNoSearchResultsState(Boolean(this.searchTerm?.trim()));
      }

      disconnectedCallback() {
        document.removeEventListener('DOMContentLoaded', this.boundInit);
        this.groups.forEach((group) => {
          group.removeEventListener('click', this.boundHandleGroupClick);
        });
        this.search?.removeEventListener('input', this.debouncedSearchInput);
        this.search?.removeEventListener('change', this.boundHandleSearchInput);
        this.search?.removeEventListener('blur', this.boundHandleSearchInput);
      }
    },
  );
}
